import React from 'react';
import type { CamelCasedPropertiesDeep } from 'type-fest';
import cx from 'clsx';
import { IconLockCategory } from '@repo/monochrome-icons';
import { FormattedMessage } from 'react-intl';
import type { Client, Organization } from 'receivable-invoices-js/react/api/models/client-portal';
import styles from './side-panel.module.css';

interface SidePanelProps {
  organization: CamelCasedPropertiesDeep<Organization>;
  client: CamelCasedPropertiesDeep<Client>;
}

export function SidePanel({ organization, client }: SidePanelProps): React.ReactNode {
  const clientName = client.companyName.length > 0 ? client.companyName : client.firstName;
  return (
    <div
      className={styles.sidePanel}
      style={
        organization.colorTheme
          ? ({
              '--sidepanel-bg': `color-mix(in srgb, ${organization.colorTheme} 5%, #fff)`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div className={styles.header}>
        {Boolean(organization.avatarUrl) && (
          <div className={styles.logoContainer}>
            <img alt={organization.legalName} data-test-logo-image src={organization.avatarUrl} />
          </div>
        )}
        <div>
          <h1 className="title-1">{organization.legalName}</h1>
          <p className={cx(styles.secondary, 'title-3')} data-test-client-name>
            <FormattedMessage id="client-portal.title.client-name" values={{ clientName }} />
          </p>
        </div>
      </div>
      <div className={styles.footerWrapper}>
        <div className={styles.footer}>
          <div className={styles.footerHeader}>
            <p className={cx(styles.secondary, 'body-1')} data-test-tax-number>
              <FormattedMessage id="client-portal.tax-number" /> {organization.legalNumber}
            </p>
          </div>
          <a
            className={cx(styles.secondary, styles.poweredLink, 'caption')}
            data-test-powered-by
            href="https://qonto.com/"
          >
            <IconLockCategory fill="currentColor" height={12} width={12} />
            <FormattedMessage id="client-portal.powered-by" />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx -- "Qonto" is a brand name  */}
            <strong>Qonto</strong>
          </a>
        </div>
      </div>
    </div>
  );
}
